<template>
  <v-container v-if="!massImportStatus" class="pa-6" fluid>
    <v-row class="ma-0">
      <v-col cols="12" class="d-flex align-center">
        <h4>{{ $t('modules.examMassScheduling.massImportExamSchedule.title') }}</h4>
        <v-tooltip bottom max-width="250">
          <template v-slot:activator="{ on, attrs }">
            <v-icon class="ml-2" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
          </template>
          <span>{{ $t('modules.examMassScheduling.massImportExamSchedule.tooltip') }}</span>
        </v-tooltip>
      </v-col>
      <v-col class="py-0" cols="12">
        <a href="/templates/inscripcion-masiva-examenes.xlsx" download>
          <Button
            outlined
            icon="mdi-download"
            :text="$t('actions.download_template')"
          />
        </a>
      </v-col>
    </v-row>

    <v-row class="ma-0">
      <v-col class="d-flex flex-column justify-center">
        <span class="caption grey--text text--darken-1">
          {{ $t('modules.examMassScheduling.massImportExamSchedule.examCategory.label') }}
        </span>
        <span class="subtitle-1 font-weight-medium mt-1">
          {{ $t('modules.examMassScheduling.massImportExamSchedule.examCategory.fixedLegend') }}
        </span>
      </v-col>
    </v-row>

    <v-row class="ma-0" v-show="!sendButton.loading">
      <v-col>
        <DropFiles
          :key="dropFilesKey"
          extension=".xls,.xlsx"
          limitSizeBytes="2000000"
          v-model="file"
          type-file="excel"
        />
      </v-col>
    </v-row>

    <v-row class="ma-0">
      <v-col class="d-flex justify-end align-center">
        <Button
          white
          class="ma-4"
          :text="$t('actions.close')"
          :disabled="sendButton.loading"
          @clicked="closeConfirmation"
        />
        <Button
          class="ma-4"
          :error="sendButton.error"
          :success="sendButton.success"
          :loading="sendButton.loading"
          :disabled="!selectedCeo || !file"
          :text="$t('actions.load')"
          :errorText="$t('actions.cancel')"
          :successText="$t('actions.loaded')"
          @clicked="confirmationPopup"
        />
      </v-col>
    </v-row>
  </v-container>
  <div v-else class="pa-6">
    <v-row v-if="!enrollStatus" class="ma-0">
      <v-col cols="12">
        <h4 class="mb-4">{{ $t('modules.examMassScheduling.massImportExamSchedule.messages.prevalidateSuccess') }}</h4>
        <div class="mass-scheduling-results overflow-y-auto" style="max-height: 400px;">
          <div v-if="validRows.length > 0" class="mb-6">
            <h5 class="subtitle-1 font-weight-medium mb-2">{{ $t('modules.examMassScheduling.table.validSection.title') }}</h5>
            <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th scope="col">{{ $t('modules.examMassScheduling.table.headers.legajo') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in validRows" :key="`valid-${row.legajo ?? idx}-${idx}`">
                  <td>{{ row.legajo }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
        <div v-if="invalidRows.length > 0">
          <h5 class="subtitle-1 font-weight-medium mb-2">{{ $t('modules.examMassScheduling.table.invalidSection.title') }}</h5>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th scope="col">{{ $t('modules.examMassScheduling.table.headers.legajo') }}</th>
                  <th scope="col">{{ $t('modules.examMassScheduling.table.headers.error') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in invalidRows" :key="`invalid-${row.legajo ?? idx}-${idx}`">
                  <td>{{ row.legajo }}</td>
                  <td>{{ row.error }}</td>
                </tr>
              </tbody>
            </template>
            </v-simple-table>
          </div>
          <div v-if="validRows.length === 0 && invalidRows.length === 0" class="caption grey--text">
            {{ $t('modules.examMassScheduling.table.validSection.empty') }}
          </div>
        </div>
      </v-col>
      <v-col cols="12" class="d-flex justify-end">
        <Button
          white
          class="ma-4"
          :text="$t('actions.close')"
          :disabled="enrollButton.loading"
          @clicked="handleClose"
        />
        <Button
          class="ma-4"
          :loading="enrollButton.loading"
          :success="enrollButton.success"
          :error="enrollButton.error"
          :disabled="validRows.length === 0"
          :text="$t('modules.examMassScheduling.massImportExamSchedule.confirmButton')"
          :successText="$t('actions.loaded')"
          :errorText="$t('actions.cancel')"
          @end="resetEnrollButton"
          @clicked="confirmEnrollment"
        />
      </v-col>
    </v-row>
    <SuccessMessage
      v-else-if="enrolledFailedRows.length === 0"
      :type="successMessage.type"
      :title="successMessage.title"
      :actionPrimary="successMessage.actionPrimary"
      :actionSecondary="successMessage.actionSecondary"
    />
    <v-row v-else class="ma-0">
      <v-col cols="12">
        <h4 class="mb-2">{{ $t('modules.examMassScheduling.massImportExamSchedule.enrollResults.title') }}</h4>
        <div class="d-flex align-center mb-4">
          <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
          <span class="body-2 mr-4">{{ enrolledSuccessRows.length }} {{ $t('modules.examMassScheduling.massImportExamSchedule.enrollResults.successful') }}</span>
          <v-icon small color="error" class="mr-1">mdi-close-circle</v-icon>
          <span class="body-2">{{ enrolledFailedRows.length }} {{ $t('modules.examMassScheduling.massImportExamSchedule.enrollResults.failed') }}</span>
        </div>
        <div class="overflow-y-auto" style="max-height: 400px;">
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th scope="col">{{ $t('modules.examMassScheduling.table.headers.legajo') }}</th>
                  <th scope="col">{{ $t('modules.examMassScheduling.table.headers.status') }}</th>
                  <th scope="col">{{ $t('modules.examMassScheduling.table.headers.error') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in enrollmentResults" :key="`enroll-${row.legajo}-${idx}`">
                  <td>{{ row.legajo }}</td>
                  <td>
                    <v-icon v-if="row.valid" small color="success">mdi-check-circle</v-icon>
                    <v-icon v-else small color="error">mdi-close-circle</v-icon>
                  </td>
                  <td class="caption">{{ row.error || '' }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </v-col>
      <v-col cols="12" class="d-flex justify-end">
        <Button
          white
          class="ma-4"
          :text="$t('actions.close')"
          @clicked="emitClose"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Button from '@/components/Button/Button';
import DropFiles from '@/components/DropFiles/DropFiles';
import SuccessMessage from '@/components/SuccessMessage/SuccessMessage';
import { $examenes } from '../../Services';

const EXAM_CATEGORY_FINAL = 466;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const [, base64] = reader.result.split(',');
      resolve(base64 || reader.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default {
  name: 'MassImportExamSchedule',
  components: {
    Button,
    DropFiles,
    SuccessMessage,
  },
  props: {
    drawerOpen: { type: Boolean, default: false },
  },
  data() {
    return {
      file: null,
      dropFilesKey: 0,
      massImportStatus: false,
      enrollStatus: false,
      selectedCeo: null,
      validRows: [],
      invalidRows: [],
      enrollmentResults: [],
      sendButton: {
        error: false,
        success: false,
        loading: false,
      },
      enrollButton: {
        error: false,
        success: false,
        loading: false,
      },
    };
  },
  mounted() {
    this.setDefaultCeo();
  },
  computed: {
    enrolledFailedRows() {
      return this.enrollmentResults.filter((r) => !r.valid);
    },
    enrolledSuccessRows() {
      return this.enrollmentResults.filter((r) => r.valid);
    },
    successMessage() {
      if (this.enrollButton.error) {
        return {
          title: this.$t('modules.examMassScheduling.massImportExamSchedule.messages.enrollError'),
          type: 'error',
          actionPrimary: {
            text: this.$t('actions.goBack'),
            callback: () => {
              this.enrollStatus = false;
              this.resetEnrollButton();
            },
          },
        };
      }
      return {
        title: this.$t('modules.examMassScheduling.massImportExamSchedule.messages.enrollSuccess'),
        type: 'success',
        actionPrimary: {
          text: this.$t('actions.close'),
          callback: () => this.emitClose(),
        },
      };
    },
  },
  methods: {
    setDefaultCeo() {
      this.selectedCeo = this.$isTeclab ? '369' : '546';
    },
    confirmationPopup() {
      if (!this.file) {
        this.$store.dispatch('commons/openPopUp', {
          title: this.$t('modules.examMassScheduling.massImportExamSchedule.messages.noFiles'),
          actionPrimary: { text: this.$t('actions.accept'), callback() {} },
          icon: { color: 'warning', name: 'mdi-alert' },
          color: 'primary',
        });
        return;
      }

      this.$store.dispatch('commons/openPopUp', {
        title: this.$t('modules.examMassScheduling.massImportExamSchedule.messages.sendConfirmation'),
        content: [{ value: this.file.name }],
        actionPrimary: { text: this.$t('actions.send'), callback: () => this.sendFile() },
        actionSecondary: { text: this.$t('actions.cancel'), callback() {} },
        icon: { color: 'warning', name: 'mdi-alert' },
        color: 'primary',
      });
    },
    closeConfirmation() {
      if (!this.file) {
        this.emitClose();
        return;
      }

      this.$store.dispatch('commons/openPopUp', {
        title: this.$t('modules.examMassScheduling.massImportExamSchedule.messages.pendingFiles'),
        actionPrimary: { text: this.$t('actions.yes'), callback: () => this.emitClose() },
        actionSecondary: { text: this.$t('actions.no'), callback() {} },
        icon: { color: 'warning', name: 'mdi-alert' },
        color: 'primary',
      });
    },
    emitClose() {
      const items = this.enrollStatus && this.enrollmentResults.length
        ? this.enrollmentResults.map((r, i) => ({
            legajo: r.legajo,
            valid: r.valid,
            error: r.error || '',
            id: `enroll-${r.legajo}-${i}`,
          }))
        : [];
      this.$emit('close', items);
    },
    handleClose() {
      if (this.validRows.length > 0 && !this.enrollStatus) {
        this.$store.dispatch('commons/openPopUp', {
          title: this.$t('modules.examMassScheduling.massImportExamSchedule.messages.pendingFiles'),
          actionPrimary: { text: this.$t('actions.yes'), callback: () => this.emitClose() },
          actionSecondary: { text: this.$t('actions.no'), callback() {} },
          icon: { color: 'warning', name: 'mdi-alert' },
          color: 'primary',
        });
      } else {
        this.emitClose();
      }
    },
    resetButtonValues() {
      this.sendButton.error = false;
      this.sendButton.success = false;
      this.sendButton.loading = false;
    },
    resetEnrollButton() {
      this.enrollButton.error = false;
      this.enrollButton.success = false;
      this.enrollButton.loading = false;
    },
    async sendFile() {
      try {
        this.sendButton.loading = true;
        this.sendButton.error = false;
        const base64File = await fileToBase64(this.file);

        const body = {
          file: base64File,
          ceoId: this.selectedCeo,
          examCategory: EXAM_CATEGORY_FINAL,
        };

        const { data } = await $examenes.prevalidateMassScheduling(body);
        this.validRows = data.validRows || [];
        this.invalidRows = data.invalidRows || [];
        this.massImportStatus = true;
        this.sendButton.success = true;
      } catch (error) {
        this.sendButton.error = true;
        this.$store.dispatch('commons/openPopUp', {
          title: this.$t('modules.examMassScheduling.massImportExamSchedule.messages.prevalidateError'),
          content: error?.message ? [{ value: error.message }] : null,
          actionPrimary: { text: this.$t('actions.accept'), callback() {} },
          icon: { color: 'error', name: 'mdi-close' },
          color: 'primary',
        });
      } finally {
        this.sendButton.loading = false;
      }
    },
    async confirmEnrollment() {
      if (this.validRows.length === 0) return;

      try {
        this.enrollButton.loading = true;
        this.enrollButton.error = false;

        const body = {
          examCategory: EXAM_CATEGORY_FINAL,
          rows: this.validRows.map((r) => ({
            legajo: r.legajo,
            mesaId: r.mesaId,
            studentSubjectId: r.studentSubjectId,
          })),
        };

        const { data } = await $examenes.bulkEnroll(body);

        this.enrollmentResults = [
          ...(data.successful || []).map((legajo) => ({ legajo, valid: true })),
          ...(data.failed || []).map((row) => ({ legajo: row.legajo, valid: false, error: row.error })),
        ];

        this.enrollButton.success = true;
        this.enrollStatus = true;
      } catch (error) {
        this.enrollButton.error = true;
        this.$store.dispatch('commons/openPopUp', {
          title: this.$t('modules.examMassScheduling.massImportExamSchedule.messages.enrollError'),
          content: error?.message ? [{ value: error.message }] : null,
          actionPrimary: { text: this.$t('actions.accept'), callback() {} },
          icon: { color: 'error', name: 'mdi-close' },
          color: 'primary',
        });
      } finally {
        this.enrollButton.loading = false;
      }
    },
  },
  watch: {
    drawerOpen(val) {
      if (val) {
        this.setDefaultCeo();
      }
      if (!val) {
        this.emitClose();
        setTimeout(() => {
          this.file = null;
          this.dropFilesKey += 1;
          this.selectedCeo = null;
          this.massImportStatus = false;
          this.enrollStatus = false;
          this.validRows = [];
          this.invalidRows = [];
          this.enrollmentResults = [];
          this.resetButtonValues();
          this.resetEnrollButton();
        }, 600);
      }
    },
  },
};
</script>
